import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Wrench, LayoutGrid, List, CalendarRange, CalendarDays, Send, Search, MessageCircle, CheckCircle2 } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { Input } from "@/components/ui/input";
import { useShopMeta } from "@/hooks/useCurrencySymbol";
import { useCollectionSend } from "@/hooks/useCollectionSend";
import { useWorkshopJobs } from "@/hooks/useWorkshopJobs";
import { VehicleChip, DueDate, dayTone } from "@/components/customers/WorkshopBoard";
import WorkshopTimeline from "@/components/workshop/WorkshopTimeline";
import { daysInShop, daysOverdue, agoText, openReadyWhatsApp, todayIso } from "@/lib/collection";
import { splitSavedQuote, fmtDate } from "@/lib/quoteText";
import { toast } from "sonner";

// Short job description for a quote: "Full respray · Premium · Int + Ext" or "4 panels · 1 service"
export function jobLabel(qt) {
  const fr = qt.builder_state?.fullRespray;
  if (fr && fr.scope && fr.scope !== "off") return `Full respray · ${fr.tier === "premium" ? "Premium" : "Standard"} · ${fr.scope === "exterior" ? "Exterior" : "Int + Ext"}`;
  const { panels, services } = splitSavedQuote(qt);
  const parts = [];
  if (panels.length) parts.push(`${panels.length} panel${panels.length === 1 ? "" : "s"}`);
  if (services.length) parts.push(`${services.length} service${services.length === 1 ? "" : "s"}`);
  if (qt.parts?.length) parts.push(`${qt.parts.length} part${qt.parts.length === 1 ? "" : "s"}`);
  return parts.join(" · ") || "Job";
}

const FILTERS = [
  { key: "all", en: "All", zh: "全部", test: () => true },
  { key: "overdue", en: "Overdue", zh: "逾期", test: (q) => daysOverdue(q.expected_done) > 0 },
  { key: "today", en: "Due today", zh: "今日到期", test: (q) => q.expected_done === todayIso() },
  { key: "unnotified", en: "Not notified", zh: "未通知", test: (q) => !q.ready_notified_at },
  { key: "long", en: "5d+ in shop", zh: "超5天", test: (q) => daysInShop(q) >= 5 },
];
const SORTS = [["days", "Longest in shop"], ["due", "Due date"], ["customer", "Customer"]];

function TableView({ items, shop, actions }) {
  const th = "text-left text-[10px] uppercase tracking-wider text-slate-500 font-semibold px-3 py-2 whitespace-nowrap";
  const td = "px-3 py-2.5 text-sm align-middle";
  return (
    <div className="rounded-2xl border border-slate-800 bg-[#151D2E] overflow-x-auto" data-testid="workshop-table">
      <table className="w-full min-w-[860px]">
        <thead className="border-b border-slate-800"><tr><th className={th}>Plate</th><th className={th}>Vehicle</th><th className={th}>Customer</th><th className={th}>Job</th><th className={th}>In shop</th><th className={th}>Due</th><th className={th}>Notified</th><th className={`${th} text-right`}>Actions</th></tr></thead>
        <tbody className="divide-y divide-slate-800/70">
          {items.map((q) => {
            const d = daysInShop(q); const late = daysOverdue(q.expected_done);
            return (
              <tr key={q.id} className={`hover:bg-slate-900/40 ${late > 0 ? "bg-rose-500/5" : ""}`} data-testid={`workshop-row-${q.id}`}>
                <td className={`${td} font-mono-num font-bold text-slate-100`}>{q.plate || "—"}</td>
                <td className={`${td} text-slate-300`}>{q.vehicle || "—"}{q.paint_code ? <span className="text-slate-500 text-xs"> · {q.paint_code}</span> : null}</td>
                <td className={td}><Link to={`/quotes?q=${encodeURIComponent(q.quote_no)}`} className="hover:text-amber-300">{q.customer_name || "Walk-in"}</Link><div className="text-[11px] text-slate-500 font-mono-num">{q.quote_no} · approved {fmtDate(q.status_changed_at || q.created_at)}</div></td>
                <td className={`${td} text-slate-300 text-xs`}>{jobLabel(q)}</td>
                <td className={td}><span className={`rounded-full border px-2 py-0.5 text-[11px] font-bold font-mono-num ${dayTone(d)}`}>{d}d</span></td>
                <td className={td}><DueDate qt={q} late={late} onDue={actions.setDue} /></td>
                <td className={`${td} text-xs`}>{q.ready_notified_at ? <span className="text-emerald-300">{agoText(q.ready_notified_at)}</span> : <span className="text-slate-600">—</span>}</td>
                <td className={`${td} text-right whitespace-nowrap`}>
                  <button onClick={() => { if (!openReadyWhatsApp(q, shop)) return toast.error("No phone number on this quote"); actions.notified(q); }} data-testid={`workshop-row-ready-${q.id}`} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-semibold bg-emerald-700 hover:bg-emerald-600 text-white mr-1.5"><MessageCircle className="w-3 h-3" /> {q.ready_notified_at ? "Re-send" : "Ready"}</button>
                  <button onClick={() => actions.collected(q)} data-testid={`workshop-row-collected-${q.id}`} title="Status only — no message" className="inline-flex items-center gap-1 h-7 px-2.5 rounded-md text-xs font-semibold border border-slate-700 bg-slate-900/60 text-slate-200 hover:bg-slate-800"><CheckCircle2 className="w-3 h-3" /> Collected</button>
                  <button onClick={() => actions.collected(q, { notify: true })} data-testid={`workshop-row-collected-notify-${q.id}`} title="Collected + WhatsApp the customer" className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-emerald-700/50 bg-emerald-900/20 text-emerald-300 hover:bg-emerald-900/40"><MessageCircle className="w-3 h-3" /></button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function Workshop() {
  const shop = useShopMeta();
  const { promptSend, element: collectionPrompt } = useCollectionSend(shop);
  const { items, loading, notified, setDue, collected, sendSummary } = useWorkshopJobs(shop, undefined, promptSend);
  const [view, setView] = useState(() => localStorage.getItem("hlh_workshop_view") || "grid");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("days");
  const [q, setQ] = useState("");
  const changeView = (v) => { setView(v); localStorage.setItem("hlh_workshop_view", v); };

  const shown = useMemo(() => {
    const f = FILTERS.find((x) => x.key === filter) || FILTERS[0];
    const needle = q.trim().toLowerCase();
    const list = items.filter(f.test).filter((x) => !needle || [x.plate, x.vehicle, x.customer_name, x.quote_no].some((v) => (v || "").toLowerCase().includes(needle)));
    const by = { days: (a, b) => daysInShop(b) - daysInShop(a), due: (a, b) => (a.expected_done || "9999").localeCompare(b.expected_done || "9999"), customer: (a, b) => (a.customer_name || "").localeCompare(b.customer_name || "") };
    return [...list].sort(by[sort]);
  }, [items, filter, sort, q]);

  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.key, items.filter(f.test).length])), [items]);
  const bays = shop.bays || 0;
  const full = bays > 0 && items.length >= bays;
  const actions = { notified, setDue, collected };
  const tab = "inline-flex items-center gap-1.5 h-9 px-2.5 sm:px-3 rounded-md text-xs sm:text-sm font-semibold text-slate-400 data-[on=true]:bg-amber-500 data-[on=true]:text-slate-950 hover:text-slate-200 transition-colors";

  const body = (() => {
    if (loading) return <div className="text-slate-500 py-16 text-center">Loading workshop…</div>;
    if (items.length === 0) {
      return (
        <div className="rounded-2xl border border-slate-800 bg-[#151D2E] py-16 text-center space-y-2" data-testid="workshop-empty">
          <Wrench className="w-8 h-8 mx-auto text-slate-600" />
          <p className="text-slate-300 font-semibold">No vehicles in the workshop</p>
          <p className="text-slate-500 text-sm">Approving a quote in the <Link to="/quotes" className="text-amber-300 hover:underline">Activity log</Link> puts the vehicle here.</p>
        </div>
      );
    }
    if (shown.length === 0) return <div className="rounded-2xl border border-slate-800 bg-[#151D2E] py-12 text-center text-slate-500 text-sm" data-testid="workshop-no-match">Nothing matches this filter.</div>;
    if (view === "grid") {
      return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="workshop-grid">
          {shown.map((qt) => <VehicleChip key={qt.id} qt={qt} shop={shop} onCollected={collected} onNotified={notified} onDue={setDue} className="w-full" />)}
        </div>
      );
    }
    if (view === "table") return <TableView items={shown} shop={shop} actions={actions} />;
    return <WorkshopTimeline items={shown} bays={bays} onDue={setDue} />;
  })();
  return (
    <div className="min-h-screen workshop-bg text-slate-100">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="workshop-page">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-amber-400/90 font-semibold">Work in progress · 进行中</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-1 flex items-center gap-3"><Wrench className="w-7 h-7 text-rose-300" /> Workshop <span className="font-zh text-amber-300/70 text-2xl">车间</span></h1>
            <p className="text-slate-400 text-sm mt-1" data-testid="workshop-page-summary">
              <span className={`font-mono-num font-bold ${full ? "text-rose-300" : "text-slate-100"}`}>{bays ? `${items.length} / ${bays} bays` : `${items.length} vehicle${items.length === 1 ? "" : "s"}`}</span>
              {full && <span className="ml-2 rounded-full bg-rose-600 text-white text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider">Workshop full · 满位</span>}
              {counts.overdue > 0 && <span className="ml-2 text-rose-300">{counts.overdue} overdue</span>}
              <span className="text-slate-600"> · due dates are a guide only — jobs close when you tap Collected</span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Plate, customer, model…" data-testid="workshop-search" className="pl-9 w-56 bg-slate-950/60 border-slate-700" /></div>
            <button onClick={sendSummary} data-testid="workshop-page-summary-btn" className="inline-flex items-center gap-1.5 h-10 px-3.5 rounded-md text-sm font-semibold bg-emerald-700 hover:bg-emerald-600 text-white"><Send className="w-4 h-4" /> Morning summary</button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2" data-testid="workshop-filters">
            {FILTERS.map((f) => (
              <button key={f.key} data-on={filter === f.key} onClick={() => setFilter(f.key)} data-testid={`workshop-filter-${f.key}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${f.key === "overdue" ? "border-rose-500/50 text-rose-200 data-[on=true]:bg-rose-600 data-[on=true]:text-white" : "border-slate-700 text-slate-300 data-[on=true]:bg-amber-500 data-[on=true]:border-amber-500 data-[on=true]:text-slate-950"}`}>
                {f.en} <span className="font-zh opacity-70">{f.zh}</span> <span className="font-mono-num opacity-80">{counts[f.key]}</span>
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="workshop-sort" className="h-9 rounded-md border border-slate-700 bg-slate-950/60 text-xs text-slate-200 px-2">
              {SORTS.map(([k, l]) => <option key={k} value={k}>Sort: {l}</option>)}
            </select>
            <div className="flex rounded-lg border border-slate-700 bg-slate-950/60 p-0.5" data-testid="workshop-view-switch">
              <button data-on={view === "grid"} onClick={() => changeView("grid")} data-testid="workshop-view-grid" className={tab}><LayoutGrid className="w-4 h-4" /> Grid</button>
              <button data-on={view === "table"} onClick={() => changeView("table")} data-testid="workshop-view-table" className={tab}><List className="w-4 h-4" /> Table</button>
              <button data-on={view === "timeline"} onClick={() => changeView("timeline")} data-testid="workshop-view-timeline" className={tab}><CalendarRange className="w-4 h-4" /> Timeline</button>
              <Link to="/calendar" data-testid="workshop-view-calendar" className={`${tab} text-slate-400 hover:text-slate-200`}><CalendarDays className="w-4 h-4" /> Calendar</Link>
            </div>
          </div>
        </div>

        {body}
      </main>
      {collectionPrompt}
    </div>
  );
}
